import { loadMediaWithSource, type LoadMediaOptions } from './mediaLoader';
import { getCachedClip, hasCachedClip, putCachedClip } from './editorMediaStore';
import { stableMediaKey } from './workspaceEdit';

/** Where a clip's bytes came from this time: already on this device, the auto-save folder, or a download. */
export type ClipOrigin = 'device' | 'folder' | 'download';

export interface LoadedClip {
    blob: Blob;
    mimeType: string;
    origin: ClipOrigin;
}

export interface LoadClipOptions extends Omit<LoadMediaOptions, 'network'> {
    /** Whether a clip that is not on this device may be downloaded. Without it a 'not-local' MediaLoadError is thrown. */
    allowDownload: boolean;
}

/** Where a clip is kept in this browser: per signed-in account and file. Null for addresses that only live for this session. */
export const clipCacheKey = (ref: string, userId?: string): string | null => {
    const stable = stableMediaKey(ref);
    return stable ? `${userId || 'guest'}:${stable}` : null;
};

/** Whether this browser keeps a copy of the clip, without reading it. */
export const isClipKept = async (ref: string, userId?: string): Promise<boolean> => {
    const key = clipCacheKey(ref, userId);
    return !!key && await hasCachedClip(key).catch(() => false);
};

/** Loads in progress, so two requests for the same clip (a thumbnail and an Add, two editors) share one download. */
const inProgress = new Map<string, Promise<LoadedClip>>();

const load = async (ref: string, options: LoadClipOptions, key: string | null): Promise<LoadedClip> => {
    if (key) {
        const kept = await getCachedClip(key).catch(() => null);
        if (kept) return { ...kept, origin: 'device' };
    }
    const { allowDownload, ...loadOptions } = options;
    const loaded = await loadMediaWithSource(ref, { ...loadOptions, network: allowDownload });
    if (key && loaded.source !== 'folder') {
        await putCachedClip(key, loaded.blob, loaded.mimeType).catch(() => undefined);
    }
    return {
        blob: loaded.blob,
        mimeType: loaded.mimeType,
        origin: loaded.source === 'folder' ? 'folder' : loaded.source === 'network' ? 'download' : 'device',
    };
};

/**
 * The bytes of a clip that lives on the canvas, from this device whenever possible: the
 * editor's own copy, then the local auto-save folder and this session's memory, and only
 * then a download. A download, or bytes that only this session holds, are kept so the
 * clip is never downloaded again in this browser. A clip read from the auto-save folder
 * is not copied, since it is already on disk. Copies are kept per signed-in account, so
 * another person using this browser is never served them. Media whose address only
 * lives for this session (blob: and data: URLs) has no lasting key; the caller keeps
 * that itself.
 */
export const loadEditorClip = (ref: string, options: LoadClipOptions): Promise<LoadedClip> => {
    const key = clipCacheKey(ref, options.userId);
    if (!key) return load(ref, options, null);
    const shareKey = `${key}|${options.allowDownload ? 'download' : 'local'}`;
    const pending = inProgress.get(shareKey);
    if (pending) return pending;
    const promise = load(ref, options, key).finally(() => inProgress.delete(shareKey));
    inProgress.set(shareKey, promise);
    return promise;
};

/** A kept or local copy of a clip, without downloading anything; null when there is none. */
export const findLocalClip = async (ref: string, options: Omit<LoadClipOptions, 'allowDownload'>): Promise<LoadedClip | null> => {
    try {
        return await loadEditorClip(ref, { ...options, allowDownload: false });
    } catch {
        return null;
    }
};
